import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Group,
  NumberFormatter,
  Paper,
  SegmentedControl,
  Skeleton,
  Table,
  Text,
} from "@mantine/core";
import { BarChart } from "@mantine/charts";
import { getModelCosts } from "../api";

const MODEL_COLORS = [
  "indigo.6",
  "teal.6",
  "orange.6",
  "grape.6",
  "cyan.6",
  "pink.6",
  "lime.6",
  "yellow.6",
  "violet.6",
  "blue.6",
  "red.6",
  "green.6",
  "gray.6",
];

const METRIC_OPTIONS = [
  { label: "Total cost", value: "cost" },
  { label: "Avg $/call", value: "avgCost" },
  { label: "Calls", value: "calls" },
];

function formatPeriod(iso, granularity) {
  const d = new Date(iso);
  const opts = { month: "short", day: "numeric", timeZone: "UTC" };
  if (granularity === "hour") {
    return d.toLocaleString(undefined, {
      ...opts,
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  if (granularity === "week") {
    return `Week of ${d.toLocaleDateString(undefined, opts)}`;
  }
  return d.toLocaleDateString(undefined, opts);
}

function cellValue(cell, metric) {
  if (!cell) return 0;
  if (metric === "avgCost") return cell.avgCostUsd ?? 0;
  if (metric === "calls") return cell.calls ?? 0;
  return cell.costUsd ?? 0;
}

function formatValue(value, metric) {
  if (value == null || value === 0) return null;
  if (metric === "calls") {
    return (
      <NumberFormatter value={value} thousandSeparator decimalScale={0} />
    );
  }
  const scale = value >= 1 ? 2 : 4;
  return (
    <NumberFormatter
      prefix="$"
      value={value}
      thousandSeparator
      decimalScale={scale}
    />
  );
}

// Mantine Charts and Recharts read "." in a series name as a nested path.
// "grok-4.6" is then drawn as "6", and a zero for that day is read from the
// missing nested key and formatted as $NaN. The data key uses a fullwidth
// dot. series.label is the model id shown in the legend and the tooltip.
const CHART_DOT = "\uFF0E";

function chartSeriesKey(model) {
  return String(model).replaceAll(".", CHART_DOT);
}

function formatChartValue(value, metric) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (metric === "calls") return n.toLocaleString();
  return `$${n.toFixed(2)}`;
}

export default function ModelCostMap({
  periodDays,
  flowName = null,
  title = "Cost by model over time",
}) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [metric, setMetric] = useState("avgCost");

  useEffect(() => {
    const abort = new AbortController();
    let current = true;
    setIsLoading(true);
    getModelCosts(periodDays, flowName, abort.signal)
      .then((result) => {
        if (!current) return;
        setData(result);
        setIsLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError" || !current) return;
        console.error("Error fetching model costs:", err);
        if (current) setIsLoading(false);
      });
    return () => {
      current = false;
      abort.abort();
    };
  }, [periodDays, flowName]);

  const models = useMemo(() => data?.models ?? [], [data]);
  const colorFor = (model) =>
    MODEL_COLORS[models.indexOf(model) % MODEL_COLORS.length];

  const cellMap = useMemo(() => {
    const map = {};
    for (const c of data?.cells ?? []) {
      map[`${c.period}|${c.model}`] = c;
    }
    return map;
  }, [data]);

  const max = useMemo(() => {
    let m = 0;
    for (const c of data?.cells ?? []) {
      m = Math.max(m, cellValue(c, metric));
    }
    return m;
  }, [data, metric]);

  const rows = useMemo(() => {
    const periods = [...(data?.periods ?? [])].reverse();
    return periods.map((period) => {
      const perModel = models.map(
        (model) => cellMap[`${period}|${model}`] ?? null
      );
      const totalCost = perModel.reduce((a, c) => a + (c?.costUsd ?? 0), 0);
      const totalCalls = perModel.reduce((a, c) => a + (c?.calls ?? 0), 0);
      return { period, perModel, totalCost, totalCalls };
    });
  }, [data, models, cellMap]);

  const chartData = useMemo(
    () =>
      (data?.periods ?? []).map((period) => {
        const point = { period: formatPeriod(period, data?.granularity) };
        for (const model of models) {
          const c = cellMap[`${period}|${model}`];
          const v = cellValue(c, metric === "avgCost" ? "cost" : metric);
          point[chartSeriesKey(model)] = Number(
            v.toFixed(metric === "calls" ? 0 : 4)
          );
        }
        return point;
      }),
    [data, models, cellMap, metric]
  );

  const chartSeries = models.map((model) => ({
    name: chartSeriesKey(model),
    label: model,
    color: colorFor(model),
  }));

  const heat = (value) => {
    if (!max || !value) return undefined;
    const t = Math.min(1, value / max);
    return `color-mix(in srgb, var(--mantine-color-indigo-6) ${Math.round(
      12 + t * 55
    )}%, transparent)`;
  };

  if (isLoading) return <Skeleton height={320} mb="lg" />;
  if (!data || data.totalCalls === 0) return null;

  const stackable = metric !== "avgCost";

  return (
    <Paper withBorder p="md" radius="md" mb="lg">
      <Group justify="space-between" align="flex-end" mb="xs">
        <Box>
          <Text fw={600}>{title}</Text>
          <Text size="xs" c="dimmed">
            What each model actually served, per{" "}
            {data.granularity}. Use this to see a model switch land, and
            whether unit cost fell.
          </Text>
        </Box>
        <SegmentedControl
          size="xs"
          value={metric}
          onChange={setMetric}
          data={METRIC_OPTIONS}
        />
      </Group>

      {stackable && chartSeries.length > 0 && (
        <BarChart
          h={240}
          mb="md"
          data={chartData}
          dataKey="period"
          type="stacked"
          series={chartSeries}
          valueFormatter={(value) => formatChartValue(value, metric)}
          withLegend
        />
      )}

      <Table.ScrollContainer minWidth={480}>
        <Table striped highlightOnHover withTableBorder fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Period</Table.Th>
              {models.map((model) => (
                <Table.Th key={model} ta="right">
                  <Group gap={6} justify="flex-end" wrap="nowrap">
                    <Box
                      w={10}
                      h={10}
                      style={{
                        borderRadius: 2,
                        backgroundColor: `var(--mantine-color-${colorFor(
                          model
                        ).replace(".", "-")})`,
                      }}
                    />
                    {model}
                  </Group>
                </Table.Th>
              ))}
              <Table.Th ta="right">
                {metric === "calls" ? "Calls" : "Total"}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.period}>
                <Table.Td>
                  {formatPeriod(row.period, data.granularity)}
                </Table.Td>
                {row.perModel.map((cell, i) => {
                  const value = cellValue(cell, metric);
                  const formatted = formatValue(value, metric);
                  return (
                    <Table.Td
                      key={models[i]}
                      ta="right"
                      style={{ background: heat(value) }}
                    >
                      {formatted ?? (
                        <Text span c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                  );
                })}
                <Table.Td ta="right" fw={600}>
                  {metric === "calls"
                    ? formatValue(row.totalCalls, "calls")
                    : formatValue(row.totalCost, "cost")}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Paper>
  );
}
